import React, { useState } from 'react';
import './SeriesBookCardOffer.css';

/**
 * Series Discovery optional book-card offer
 * (#709 / #736 / #741 / #744 / #748 / #752 / #754 / #755).
 *
 * After both foundation facts exist, the partner may offer one or more
 * book cards. Accept writes a Timeline idea pin (same object Series
 * Cards reads). Edit the title, or ignore. Does not start a book.
 * After accept, a short in-talk note says it landed as an idea, not
 * a started book. A new stamp stays in view (#741).
 *
 * #754 leftover vs #752: Nobody Vetted still showed the tall
 * about-box (“What this book is about” textarea + Accept) with the
 * title hidden. #752 / PR #753 did not hold. This chrome is a
 * small Timeline-style title card: title only, no about textarea,
 * no inner scroll. The old tall form is not imported or rendered
 * here. Title and Accept share the first view. The stamped premise
 * still writes onto the Timeline pin. Leftover talk is not a
 * title (#755 / #764). A placeholder is not a title (#766).
 * An offer shows only when the title is a real book name —
 * a title-shaped proper name. Leftover talk, a last-reply
 * fragment, a question, a clause, or a stand-in / numbered
 * slot is not a title. If it cannot stamp a real book name,
 * do not show an offer (#749 / #754 / #755 / #764 / #766).
 * Not a persisted chat
 * message. Not the Focus sitting offer. Not The book's argument.
 * Not Conflict. Not the Make-cards checklist form.
 */

export const SERIES_BOOK_CARD_OFFER_ATTR = 'data-series-book-card-offer';

export const SERIES_BOOK_CARD_OFFER_TITLE_ATTR = 'data-series-book-card-offer-title';

export const SERIES_BOOK_CARD_OFFER_HINT =
  'Optional idea note — accept it onto the Timeline as an idea, edit it first, or ignore. It does not start a book.';

export const SERIES_BOOK_CARD_OFFER_ACCEPT_LABEL = 'Accept';

export const SERIES_BOOK_CARD_OFFER_SKIP_LABEL = 'Not now';

export const SERIES_BOOK_CARD_LANDED_NOTE =
  'landed as an idea on the Timeline, not a started book.';

export function seriesBookCardLandedNote(title) {
  const name = (title || '').trim();
  return name
    ? `${name} ${SERIES_BOOK_CARD_LANDED_NOTE}`
    : `That ${SERIES_BOOK_CARD_LANDED_NOTE}`;
}

// Sitting-scoped confirmations. Reload does not nag. Not a chat write.
const landedBySession = new Map();

export function clearSeriesBookCardLanded(sessionId) {
  if (sessionId) landedBySession.delete(sessionId);
  else landedBySession.clear();
}

export function readLandedCards(sessionId) {
  if (!sessionId) return {};
  return landedBySession.get(sessionId) || {};
}

export function markSeriesBookCardLanded(sessionId, card) {
  if (!sessionId || !card?.id) return;
  const prev = readLandedCards(sessionId);
  landedBySession.set(sessionId, {
    ...prev,
    [card.id]: { id: card.id, title: (card.title || '').trim() },
  });
}

export function landedCardsForOffer(offer, sessionId) {
  const landed = readLandedCards(sessionId);
  return (offer?.cards || []).filter((card) => card?.id && landed[card.id]);
}

export function declineStorageKey(sessionId) {
  return `brainstormy.seriesBookCardOffer.declined.${sessionId}`;
}

export function doneStorageKey(sessionId) {
  return `brainstormy.seriesBookCardOffer.done.${sessionId}`;
}

export function isSeriesBookCardOfferDeclined(sessionId) {
  if (!sessionId) return false;
  try {
    return window.localStorage.getItem(declineStorageKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

export function markSeriesBookCardOfferDeclined(sessionId) {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(declineStorageKey(sessionId), '1');
  } catch {
    /* private mode / quota — hide in this page either way */
  }
}

export function readDoneCardIds(sessionId) {
  if (!sessionId) return [];
  try {
    const raw = window.localStorage.getItem(doneStorageKey(sessionId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function markSeriesBookCardDone(sessionId, cardId) {
  if (!sessionId || !cardId) return;
  try {
    const next = [...new Set([...readDoneCardIds(sessionId), cardId])];
    window.localStorage.setItem(doneStorageKey(sessionId), JSON.stringify(next));
  } catch {
    /* private mode / quota */
  }
}

function foldTalk(text) {
  return String(text || '')
    .replace(/\u2019/g, "'")
    .replace(/\u2018/g, "'")
    .replace(/`/g, "'");
}

const GENERIC_OFFER_TITLES = new Set([
  'book', 'books', 'room', 'rooms', 'one', 'another', 'the first',
  'the second', 'the third', 'a book', 'a room',
  'novel', 'novels', 'a novel', 'the novel',
  'volume', 'volumes', 'installment', 'installments',
]);

const TITLE_WORD_CAP = 10;
const SMALL_TITLE_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'nor', 'of', 'on', 'in',
  'to', 'for', 'at', 'by', 'as', 'vs', 'from', 'with', 'into',
  'onto', 'over', 'under', 'via',
]);
const COMPACT_DETERMINERS = new Set(['the', 'a', 'an']);
const CLAUSE_OPENERS = new Set([
  'whose', 'who', 'whom', 'which', 'what', 'why', 'how',
  'does', 'do', 'did', 'is', 'are', 'was', 'were',
  'can', 'could', 'would', 'should', 'will',
  'because', 'although', 'unless', 'until', 'while',
  'if', 'when', 'where', 'whether', 'since',
  'and', 'but', 'so', 'or',
]);
const LEFTOVER_PRONOUNS = new Set([
  'i', 'me', 'my', 'mine', 'we', 'us', 'our',
  'you', 'your',
  'he', 'him', 'his',
  'she', 'her', 'hers',
  'they', 'them', 'their',
  'it', 'its',
]);
const AUX_OR_FINITE = new Set([
  'does', 'do', 'did', 'is', 'are', 'was', 'were',
  'can', 'could', 'would', 'should', 'will', 'shall',
  'has', 'have', 'had',
  "don't", "doesn't", "didn't", "isn't", "aren't",
  "wasn't", "weren't", "won't", "wouldn't", "couldn't",
  "shouldn't", "can't",
  "i'm", "i'd", "i'll", "i've",
]);
const CLAUSE_GLUE = new Set(['because', 'than', 'whether', 'unless']);
const LEFTOVER_QUESTION_STEM = /^(does|do|did|is|are|was|were|can|could|would|should)\s+(that|this|it)\b/i;
const LEFTOVER_TITLE_START = /^(that(?:'s|'s)?|this|it(?:'s|'s)?|those|these|(?:she|he|they)(?:'d|'s|'ll|'re))\b/i;
const LEFTOVER_THAT_CONTRACTION = /^that(?:'s|'s)\b/i;
const FIRST_PERSON_ASIDE = /\bi(?:'m|'d|'ll|'ve)?\s+(?:didn'?t|did\s+not|won'?t|will\s+not|need\s+to|just)\b/i;
const LEFTOVER_SENTENCE_TITLE = /(\bthan\b|\bbecause\b|\btells\b|[.;?])/i;
// Placeholder class (#766). A stand-in or numbered slot is
// not a spine name. After these words come off, a real name
// still has something distinctive left. Shape / class test,
// not a list of exact titles.
const PENDING_NAME_WORDS = new Set([
  'tbd', 'tba', 'tbc', 'pending', 'unknown', 'untitled', 'unnamed',
]);
const STAND_IN_WORDS = new Set([
  'working', 'temporary', 'temp', 'tentative', 'placeholder',
  'proposed', 'provisional', 'draft',
]);
const NAME_SLOT_WORDS = new Set([
  'title', 'name', 'titled', 'named',
]);
const SLOT_LABEL_WORDS = new Set([
  'book', 'novel', 'volume', 'part', 'installment', 'chapter',
]);
const EMPTY_NAME_WORDS = new Set(['no', 'none', 'yet', 'not']);
const PLACEHOLDER_CLASS_WORDS = new Set([
  ...PENDING_NAME_WORDS,
  ...STAND_IN_WORDS,
  ...NAME_SLOT_WORDS,
  ...SLOT_LABEL_WORDS,
  ...EMPTY_NAME_WORDS,
  ...COMPACT_DETERMINERS,
]);
const ORDINAL_OR_NUMBER = /^(?:#?\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|i{1,3}|iv|vi{0,3}|ix|xi{0,2}|xii)$/i;
const PENDING_PHRASE = /\b(?:to\s+be\s+(?:determined|announced|confirmed|titled|named|decided)|coming\s+soon)\b/gi;

function stripOfferTitle(title) {
  return foldTalk(title)
    .trim()
    .replace(/^\*+|\*+$/g, '')
    .replace(/^["“”']+|["“”']+$/g, '')
    .replace(/[.,;:]+$/g, '')
    .trim();
}

function bareWord(word) {
  return String(word || '').replace(/^["“”'’:,.!?()]+|["“”'’:,.!?()]+$/g, '');
}

function leadingLetter(word) {
  const match = String(word || '').match(/[A-Za-z]/);
  return match ? match[0] : '';
}

function foldedWords(title) {
  return String(title || '').split(/\s+/).map(bareWord).filter(Boolean).map((word) => word.toLowerCase());
}

function isTitleShapedProperName(title) {
  const words = String(title || '').split(/\s+/).map(bareWord).filter(Boolean);
  if (!words.length) return false;
  const letters = String(title).replace(/[^A-Za-z]/g, '');
  if (letters && letters === letters.toUpperCase()) return true;
  return words.every((word, index) => {
    const parts = word.split(/[-/]/).filter(Boolean);
    if (!parts.length) return false;
    return parts.every((part, partIndex) => {
      const folded = part.toLowerCase();
      const leading = leadingLetter(part);
      if (!leading) return true;
      const isFirst = index === 0 && partIndex === 0;
      const isLast = index === words.length - 1 && partIndex === parts.length - 1;
      if (SMALL_TITLE_WORDS.has(folded) && !isFirst && !isLast) return true;
      return leading === leading.toUpperCase();
    });
  });
}

function isCompactNounPhrase(title) {
  const words = foldedWords(title);
  if (!words.length || words.length > TITLE_WORD_CAP) return false;
  if (CLAUSE_OPENERS.has(words[0])) return false;
  if (words.some((word) => LEFTOVER_PRONOUNS.has(word))) return false;
  if (words.some((word) => AUX_OR_FINITE.has(word))) return false;
  if (words.some((word) => CLAUSE_GLUE.has(word))) return false;
  const start = COMPACT_DETERMINERS.has(words[0]) ? 1 : 0;
  return words.length > start;
}

function isLeftoverTalkShape(title) {
  const cleaned = foldTalk(title);
  if (LEFTOVER_QUESTION_STEM.test(cleaned)) return true;
  if (/\?/.test(cleaned)) return true;
  if (FIRST_PERSON_ASIDE.test(cleaned)) return true;
  if (LEFTOVER_TITLE_START.test(cleaned)) {
    const words = cleaned.split(/\s+/);
    if (words.length <= 2) return true;
    if (LEFTOVER_SENTENCE_TITLE.test(cleaned)) return true;
    if (LEFTOVER_THAT_CONTRACTION.test(cleaned)) return true;
  }
  const words = foldedWords(cleaned);
  if (!words.length) return true;
  if (
    (words[0] === 'whose' || words[0] === 'whom')
    && words.slice(1).some((word) => LEFTOVER_PRONOUNS.has(word))
  ) {
    return true;
  }
  if (
    !isTitleShapedProperName(cleaned)
    && ['whose', 'who', 'whom', 'which', 'what', 'why', 'how'].includes(words[0])
  ) {
    return true;
  }
  return false;
}

function isOrdinalOrNumber(word) {
  return ORDINAL_OR_NUMBER.test(word);
}

function isPlaceholderTitleShape(title) {
  const cleaned = foldTalk(title).replace(PENDING_PHRASE, ' ');
  const words = foldedWords(cleaned);
  if (!words.length) return true;
  const distinctive = words.filter((word) => (
    !PLACEHOLDER_CLASS_WORDS.has(word)
    && !SMALL_TITLE_WORDS.has(word)
    && !isOrdinalOrNumber(word)
  ));
  if (distinctive.length) return false;
  const usedStandIn = words.some((word) => (
    PENDING_NAME_WORDS.has(word)
    || STAND_IN_WORDS.has(word)
    || NAME_SLOT_WORDS.has(word)
    || EMPTY_NAME_WORDS.has(word)
  ));
  const usedSlot = words.some((word) => SLOT_LABEL_WORDS.has(word));
  return usedStandIn || usedSlot;
}

export function isRealBookName(title) {
  const cleaned = stripOfferTitle(title);
  if (!cleaned) return false;
  if (GENERIC_OFFER_TITLES.has(cleaned.toLowerCase())) return false;
  if (isLeftoverTalkShape(cleaned)) return false;
  if (isPlaceholderTitleShape(cleaned)) return false;
  if (isTitleShapedProperName(cleaned)) return true;
  if (isCompactNounPhrase(cleaned)) return true;
  return false;
}

export function isLeftoverOfferTitle(title) {
  return !isRealBookName(title);
}

export function hasRealOfferTitle(card) {
  return isRealBookName(card?.title);
}

export function shouldShowSeriesBookCardOffer(offer, sessionId) {
  if (!offer?.offered) return false;
  if (isSeriesBookCardOfferDeclined(sessionId)) return false;
  if (visibleOfferedCards(offer, sessionId).length > 0) return true;
  return landedCardsForOffer(offer, sessionId).length > 0;
}

export function visibleOfferedCards(offer, sessionId) {
  const done = new Set(readDoneCardIds(sessionId));
  return (offer?.cards || []).filter(
    (card) => card?.id && !done.has(card.id) && hasRealOfferTitle(card),
  );
}

export function pendingBookCardOfferStampKey(messages, sessionId) {
  if (!Array.isArray(messages)) return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const offer = messages[i]?.metadata?.series_book_card_offer;
    if (!shouldShowSeriesBookCardOffer(offer, sessionId)) continue;
    const ids = visibleOfferedCards(offer, sessionId)
      .map((card) => card.id)
      .filter(Boolean);
    if (!ids.length) continue;
    return ids.slice().sort().join(',');
  }
  return '';
}

function offerTitleRow(el) {
  if (!el?.querySelector) return el;
  return el.querySelector(`[${SERIES_BOOK_CARD_OFFER_TITLE_ATTR}]`) || el;
}

function scrollWholeOfferCardIntoPane(root, el) {
  if (typeof root.getBoundingClientRect !== 'function') return false;
  if (typeof el.getBoundingClientRect !== 'function') return false;
  if (typeof root.scrollTop !== 'number') return false;

  const pane = root.getBoundingClientRect();
  const card = el.getBoundingClientRect();
  const titleEl = offerTitleRow(el);
  const title = typeof titleEl.getBoundingClientRect === 'function'
    ? titleEl.getBoundingClientRect()
    : card;
  const paneHeight = root.clientHeight || (pane.bottom - pane.top);
  const cardHeight = card.height || (card.bottom - card.top);
  // jsdom and other degenerate layouts: let scrollIntoView handle it.
  if (paneHeight <= 0 || cardHeight <= 0) return false;

  const titleOverflow = pane.top - (title.top ?? card.top);
  const acceptOverflow = card.bottom - pane.bottom;
  if (titleOverflow <= 0 && acceptOverflow <= 0) return true;

  // Prefer the whole small title card (title + Accept).
  // If it cannot fit, pin the title at the top of the pane.
  // Never scroll down to reveal Accept if that would hide the title
  // (#754 / #752 vs #748 Accept-only leftover).
  if (cardHeight <= paneHeight) {
    const revealAcceptWouldHideTitle = titleOverflow <= 0
      && acceptOverflow > 0
      && (titleOverflow + acceptOverflow) > 0;
    root.scrollTop += titleOverflow > 0 || revealAcceptWouldHideTitle
      ? -titleOverflow
      : acceptOverflow;
    return true;
  }

  root.scrollTop -= titleOverflow;
  return true;
}

export function keepSeriesBookCardOfferInView(root) {
  if (!root?.querySelectorAll) return false;
  const nodes = root.querySelectorAll(`[${SERIES_BOOK_CARD_OFFER_ATTR}="pending"]`);
  const el = nodes[nodes.length - 1];
  if (!el) return false;
  if (scrollWholeOfferCardIntoPane(root, el)) return true;
  if (typeof el.scrollIntoView !== 'function') return false;
  // Fallback: start, not nearest/end. nearest end-aligns a card below
  // the fold and leaves the title one line above (#741 leftover).
  el.scrollIntoView({ block: 'start', inline: 'nearest' });
  return true;
}

export default function SeriesBookCardOffer({
  sessionId,
  offer,
  onAccept,
  onDecline,
}) {
  const [hidden, setHidden] = useState(() => isSeriesBookCardOfferDeclined(sessionId));
  const [doneIds, setDoneIds] = useState(() => readDoneCardIds(sessionId));
  const [landed, setLanded] = useState(() => {
    const stored = readLandedCards(sessionId);
    return (offer?.cards || [])
      .filter((card) => card?.id && stored[card.id])
      .map((card) => ({
        id: card.id,
        title: stored[card.id].title || card.title || '',
      }));
  });
  const [drafts, setDrafts] = useState(() => {
    const next = {};
    (offer?.cards || []).forEach((card) => {
      next[card.id] = {
        title: card.title || '',
        proposition: card.proposition || card.statement || '',
      };
    });
    return next;
  });
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  if (hidden) return null;

  const cards = (offer?.cards || []).filter(
    (card) => card?.id && !doneIds.includes(card.id) && hasRealOfferTitle(card),
  );
  if (cards.length === 0 && landed.length === 0) return null;

  const hideAll = () => {
    markSeriesBookCardOfferDeclined(sessionId);
    setHidden(true);
    onDecline?.();
  };

  const finishCard = (cardId) => {
    markSeriesBookCardDone(sessionId, cardId);
    setDoneIds((prev) => (prev.includes(cardId) ? prev : [...prev, cardId]));
  };

  const handleSkip = (cardId) => {
    finishCard(cardId);
    setError(null);
  };

  const handleAccept = async (card) => {
    const draft = drafts[card.id] || card;
    const next = {
      ...card,
      title: (draft.title || '').trim(),
      proposition: (draft.proposition || '').trim(),
      statement: (draft.proposition || '').trim(),
    };
    if (!next.title) {
      setError('Give this offer a title before you accept it.');
      return;
    }
    if (!next.proposition) {
      setError('This offer has no book to pin.');
      return;
    }
    setSavingId(card.id);
    setError(null);
    try {
      await onAccept?.(next);
      markSeriesBookCardLanded(sessionId, next);
      setLanded((prev) => (
        prev.some((row) => row.id === next.id)
          ? prev
          : [...prev, { id: next.id, title: next.title }]
      ));
      finishCard(card.id);
    } catch (err) {
      setError(err.message || 'Could not accept this idea note.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="series-book-card-offer"
      role="group"
      aria-label="Optional idea note"
      data-series-book-card-offer={cards.length > 0 ? 'pending' : 'landed'}
    >
      <p className="series-book-card-offer__sr-only">{SERIES_BOOK_CARD_OFFER_HINT}</p>
      {landed.length > 0 && (
        <ul className="series-book-card-offer__landed-list">
          {landed.map((card) => (
            <li key={card.id}>
              <p className="series-book-card-offer__landed" role="status">
                {seriesBookCardLandedNote(card.title)}
              </p>
            </li>
          ))}
        </ul>
      )}
      {cards.length > 0 && (
      <ul className="series-book-card-offer__cards">
        {cards.map((card) => {
          const draft = drafts[card.id] || card;
          return (
            <li key={card.id} className="series-book-card-offer__card">
              <div className="series-book-card-offer__pin">
                <label
                  className="series-book-card-offer__title-label"
                  data-series-book-card-offer-title=""
                >
                  <span className="series-book-card-offer__sr-only">Book title</span>
                  <input
                    type="text"
                    className="series-book-card-offer__title"
                    value={draft.title}
                    onChange={(event) => setDrafts((prev) => ({
                      ...prev,
                      [card.id]: { ...prev[card.id], title: event.target.value },
                    }))}
                  />
                </label>
                <div className="series-book-card-offer__doors">
                  <button
                    type="button"
                    className="series-book-card-offer__door"
                    onClick={() => handleAccept(card)}
                    disabled={savingId === card.id}
                  >
                    {SERIES_BOOK_CARD_OFFER_ACCEPT_LABEL}
                  </button>
                  <button
                    type="button"
                    className="series-book-card-offer__skip"
                    onClick={() => handleSkip(card.id)}
                    disabled={savingId === card.id}
                  >
                    {SERIES_BOOK_CARD_OFFER_SKIP_LABEL}
                  </button>
                  <button
                    type="button"
                    className="series-book-card-offer__dismiss"
                    onClick={hideAll}
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      )}
      {error && <p className="series-book-card-offer__error">{error}</p>}
    </div>
  );
}
